import type React from "react"
import { useState, useEffect, useCallback } from "react"
import { Route, Routes, useLocation, useNavigate } from "react-router-dom"
import {
  UsersIcon,
  Menu,
  LogOut,
  ChevronLeft,
  User,
  Scale,
  Building,
  X,
} from "lucide-react"
import { Button } from "../ui/button"
import { UserAvatar } from "../ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import { cn } from "../../lib/utils"

import { MyContextProvider, useMyContext } from "../../context/Context"
import { clearAuthAndRedirect } from '../FetchData'

// Import your existing components
import Leads from "../../pages/leads/Leads"
import Attorneys from "../../pages/attorneys/Attorneys"
import AttorneyDetail from "../../pages/attorneys/AttorneyDetails"
import Firms from "../../pages/firms/Firms"
import AddAttorney from "../../pages/attorneys/AddAttorney"
import AddFirm from "../../pages/firms/AddFirm"
import FirmDetails from "../../pages/firms/FirmDetails"
import AddUser from "../../pages/users/AddUser"
import LeadDetailsModern from "../../pages/leads/LeadDetails"
import AddLeads from "../../pages/leads/AddLeads"
import UserPage from "../../pages/users/Users"
import UserDetails from "../../pages/users/UserDetails"

interface NavItem {
  key: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  path: string
}

const navItems: NavItem[] = [
  { key: "leads", label: "Leads", icon: UsersIcon, path: "/app/leads" },
  { key: "attorneys", label: "Attorneys", icon: Scale, path: "/app/attorneys" },
  { key: "firms", label: "Firms", icon: Building, path: "/app/firms" },
  { key: "users", label: "Users", icon: User, path: "/app/users" },
]

function SidebarContent() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, loading } = useMyContext()
  const [screen, setScreen] = useState("leads")
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  const drawerWidth = isCollapsed ? 80 : 280

  const toggleScreen = useCallback(() => {
    const pathSegments = location.pathname.split("/")
    const currentScreen = pathSegments[2] || "leads"

    if (navItems.some((item) => item.key === currentScreen)) {
      setScreen(currentScreen)
    }
  }, [location.pathname])

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      const wasMobile = isMobile
      const newIsMobile = window.innerWidth < 768
      setIsMobile(newIsMobile)
      
      // Close mobile sidebar if switching from mobile to desktop
      if (wasMobile && !newIsMobile) {
        setIsMobileSidebarOpen(false)
      }
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [isMobile])

  // Close mobile sidebar when clicking outside
  useEffect(() => {
    if (!isMobile || !isMobileSidebarOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (!target.closest('[data-mobile-sidebar]')) {
        setIsMobileSidebarOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMobile, isMobileSidebarOpen])

  useEffect(() => {
    toggleScreen()
  }, [location.pathname, toggleScreen])

  const handleNavigation = (item: NavItem) => {
    navigate(item.path)
    setScreen(item.key)
    // Close mobile sidebar when navigating
    if (isMobile) {
      setIsMobileSidebarOpen(false)
    }
  }

  const handleLogout = () => {
    clearAuthAndRedirect()
  }

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      {!isMobile && (
        <div
          className={cn(
            "fixed left-0 top-0 z-50 h-full bg-white border-r border-slate-200 transition-all duration-300 ease-in-out shadow-lg",
            isCollapsed ? "w-20" : "w-70",
          )}
          style={{ width: `${drawerWidth}px` }}
        >
        {/* Logo Section */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200" style={{ backgroundColor: 'rgb(141, 35, 52)' }}>
          {!isCollapsed && (
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <img src="/cm-emblem.svg" alt="C&M Emblem" className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Leads Management</h1>
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className="w-8 h-8 flex items-center justify-center mx-auto">
              <img src="/cm-emblem.svg" alt="C&M Emblem" className="w-8 h-8" />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <div className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon
              const isActive = screen === item.key

              return (
                <button
                  key={item.key}
                  onClick={() => handleNavigation(item)}
                  className={cn(
                    "w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left transition-all duration-200 group",
                    isActive
                      ? "bg-slate-900 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
                  )}
                  title={isCollapsed ? item.label : undefined}
                >
                  <Icon
                    className={cn(
                      "w-5 h-5 flex-shrink-0",
                      isActive ? "text-white" : "text-slate-500 group-hover:text-slate-700",
                    )}
                  />
                  {!isCollapsed && <span className="font-medium text-sm">{item.label}</span>}
                </button>
              )
            })}
          </div>
        </nav>

        {/* Collapse Toggle */}
        <div className="p-4 border-t border-slate-200">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <Menu className="w-5 h-5" />
            ) : (
              <div className="flex items-center space-x-2">
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Collapse</span>
              </div>
            )}
          </button>
        </div>
      </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {isMobile && (
        <div
          data-mobile-sidebar
          className={cn(
            "fixed inset-0 z-50 bg-white transition-transform duration-300 ease-in-out",
            isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          {/* Mobile Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200" style={{ backgroundColor: 'rgb(141, 35, 52)' }}>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <img src="/cm-emblem.svg" alt="C&M Emblem" className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Leads Management</h1>
              </div>
            </div>
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              className="p-2 rounded-lg text-white hover:bg-opacity-20 hover:bg-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile Navigation */}
          <nav className="flex-1 p-4">
            <div className="space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon
                const isActive = screen === item.key

                return (
                  <button
                    key={item.key}
                    onClick={() => handleNavigation(item)}
                    className={cn(
                      "w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left transition-all duration-200 group",
                      isActive
                        ? "bg-slate-900 text-white shadow-sm"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
                    )}
                  >
                    <Icon
                      className={cn(
                        "w-5 h-5 flex-shrink-0",
                        isActive ? "text-white" : "text-slate-500 group-hover:text-slate-700",
                      )}
                    />
                    <span className="font-medium text-sm">{item.label}</span>
                  </button>
                )
              })}
            </div>
          </nav>

          {/* Mobile Logout */}
          <div className="p-4 border-t border-slate-200">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium text-sm">Sign out</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className={cn("flex-1", !isMobile ? "ml-20" : "")}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-4">
              {/* Mobile Hamburger Button */}
              {isMobile && (
                <button
                  onClick={() => setIsMobileSidebarOpen(true)}
                  className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors mr-2"
                >
                  <Menu className="w-5 h-5" />
                </button>
              )}
              <h2 className="text-2xl font-bold text-slate-900 capitalize">{screen}</h2>
            </div>

            <div className="flex items-center space-x-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    {loading ? (
                      <div className="h-10 w-10 rounded-full bg-slate-200 animate-pulse flex items-center justify-center">
                        <div className="h-4 w-4 bg-slate-400 rounded-full"></div>
                      </div>
                    ) : (
                      <UserAvatar user={user} size="md" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium text-sm text-slate-900">
                        {loading ? "Loading..." : `${user?.first_name || "User"} ${user?.last_name || ""}`}
                      </p>
                      <p className="text-xs text-slate-500">{loading ? "..." : user?.email || "user@example.com"}</p>
                    </div>
                  </div>

                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <Routes>
            <Route index element={<Leads />} />
            <Route path="/app/leads" element={<Leads />} />
            <Route path="/app/leads/add-leads" element={<AddLeads />} />
            <Route path="/app/leads/lead-details-modern" element={<LeadDetailsModern />} />
            <Route path="/app/attorneys" element={<Attorneys />} />
            <Route path="/app/attorneys/add-attorney-modern" element={<AddAttorney />} />
            <Route path="/app/attorneys/attorney-detail" element={<AttorneyDetail />} />
            <Route path="/app/firms" element={<Firms />} />
            <Route path="/app/firms/add-firm-modern" element={<AddFirm />} />
            <Route path="/app/firms/firm-detail" element={<FirmDetails />} />
            <Route path="/app/users" element={<UserPage />} />
            <Route path="/app/users/add-user-modern" element={<AddUser />} />
            <Route path="/app/users/user-details" element={<UserDetails />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}

export default function Sidebar() {
  return (
    <MyContextProvider>
      <SidebarContent />
    </MyContextProvider>
  )
}
